// vg-binding: P7.D-27
// vg-binding: P7.D-29
// vg-binding: P7.D-30
//
// apps/vendor/src/pages/Inventory.tsx
//
// Phase 7 Wave 15 polish — vendor inventory page (replaces PlaceholderPage).
// Three tabs: Stock | Movements | Transfers. Tab state persisted in URL
// via `?tab=stock|movements|transfers`.

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@printway/ui';

import MovementsTab from '../features/inventory/MovementsTab.js';
import StockTab from '../features/inventory/StockTab.js';
import TransfersTab from '../features/inventory/TransfersTab.js';
import { useTranslation } from '../lib/i18n/index.js';

type TabKey = 'stock' | 'movements' | 'transfers';

const TABS: Array<{ key: TabKey; labelKey: string }> = [
  { key: 'stock', labelKey: 'page.tabStock' },
  { key: 'movements', labelKey: 'page.tabMovements' },
  { key: 'transfers', labelKey: 'page.tabTransfers' },
];

function parseTab(raw: string | null): TabKey {
  if (raw === 'movements' || raw === 'transfers' || raw === 'stock') return raw;
  return 'stock';
}

export function InventoryPage(): JSX.Element {
  const { t } = useTranslation('inventory');
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<TabKey>(() => parseTab(params.get('tab')));
  const [toast, setToast] = useState<string | null>(null);

  // Sync URL → state when user navigates back/forward.
  useEffect(() => {
    const next = parseTab(params.get('tab'));
    if (next !== tab) setTab(next);
  }, [params, tab]);

  const handleTabChange = useCallback(
    (next: TabKey) => {
      setTab(next);
      const nextParams = new URLSearchParams(params);
      if (next === 'stock') nextParams.delete('tab');
      else nextParams.set('tab', next);
      // dbg (2026-07-07) UAT #2133 — push (not replace) so each tab switch is
      // its own history entry; otherwise browser Back skips past Inventory
      // entirely ("không back được"). The URL→state effect above restores the
      // right tab on back/forward.
      setParams(nextParams, { replace: false });
    },
    [params, setParams],
  );

  // Auto-clear toast after 4s.
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  return (
    <main
      data-testid="inventory-page-root"
      className="flex flex-col gap-4 px-6 py-6"
    >
      <PageHeader title={t('page.title')} />

      <nav
        data-testid="inventory-tabs"
        className="flex gap-1 border-b border-pw-border"
      >
        {TABS.map((tabDef) => (
          <button
            key={tabDef.key}
            type="button"
            data-testid={`inventory-tab-${tabDef.key}`}
            onClick={() => handleTabChange(tabDef.key)}
            aria-current={tab === tabDef.key ? 'page' : undefined}
            className={
              tab === tabDef.key
                ? 'border-b-2 border-pw-primary px-4 py-2 text-sm font-semibold text-pw-text'
                : 'border-b-2 border-transparent px-4 py-2 text-sm font-medium text-pw-text-muted hover:text-pw-text'
            }
          >
            {t(tabDef.labelKey)}
          </button>
        ))}
      </nav>

      {tab === 'stock' ? <StockTab onToast={setToast} /> : null}
      {tab === 'movements' ? <MovementsTab /> : null}
      {tab === 'transfers' ? <TransfersTab onToast={setToast} /> : null}

      {toast ? (
        <div
          data-testid="inventory-toast"
          role="status"
          className="fixed bottom-6 right-6 z-50 rounded-md border border-pw-border bg-pw-bg px-4 py-3 text-sm text-pw-text shadow-lg"
        >
          {toast}
        </div>
      ) : null}
    </main>
  );
}

export default InventoryPage;
